import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import type {
  AncestryEnum,
  AdoptionStatusEnum,
  FamilyMemberEnum,
  YesNoUnknown,
} from "./options";

// ============================================================================
// Types
// ============================================================================

export interface FamilyHistoryState {
  // Ethnicity
  ancestryHistory: AncestryEnum[];
  // DNA testing
  hasDoneDNAAncestryReport: boolean | null;
  dnaAncestryDocumentId: string | null;
  dnaAncestryReportDate: string | null;
  dontHaveReport: boolean;
  // Adoption
  adoptionStatus: AdoptionStatusEnum | null;
  // Family infertility
  hasRelativeFacingFertilityIssues: YesNoUnknown | null;
  familyMemberWithFertilityIssues: FamilyMemberEnum[];
  // DES
  hasMotherTakenDESDuringPregnancy: YesNoUnknown | null;
  // Diseases
  diseasesMothersSide: string | null;
  diseasesFathersSide: string | null;
  // Original API entry IDs (for delete-then-recreate on save)
  _originalAncestryHistoryIds: string[];
  _originalFamilyMemberIds: string[];
}

type PersistedFamilyHistory = {
  ancestryHistory?: { id?: string; ancestry?: AncestryEnum }[];
  hasDoneDNAAncestryReport?: boolean | null;
  dnaAncestryDocumentId?: string | null;
  dnaAncestryReportDate?: string | null;
  adoptionStatus?: AdoptionStatusEnum | null;
  hasRelativeFacingFertilityIssues?: YesNoUnknown | null;
  familyMemberWithFertilityIssues?: {
    id?: string;
    familyMember?: FamilyMemberEnum;
  }[];
  hasMotherTakenDESDuringPregnancy?: YesNoUnknown | null;
  diseasesMothersSide?: string | null;
  diseasesFathersSide?: string | null;
};

interface FamilyHistoryContextType {
  state: FamilyHistoryState;
  updateState: (updates: Partial<FamilyHistoryState>) => void;
}

// ============================================================================
// Context
// ============================================================================

const FamilyHistoryContext = createContext<
  FamilyHistoryContextType | undefined
>(undefined);

const initialState: FamilyHistoryState = {
  ancestryHistory: [],
  hasDoneDNAAncestryReport: null,
  dnaAncestryDocumentId: null,
  dnaAncestryReportDate: null,
  dontHaveReport: false,
  adoptionStatus: null,
  hasRelativeFacingFertilityIssues: null,
  familyMemberWithFertilityIssues: [],
  hasMotherTakenDESDuringPregnancy: null,
  diseasesMothersSide: null,
  diseasesFathersSide: null,
  _originalAncestryHistoryIds: [],
  _originalFamilyMemberIds: [],
};

export function FamilyHistoryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: patient, isLoading } = usePatient();
  const [state, setState] = useState<FamilyHistoryState>(initialState);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (isLoading || !patient || hydratedRef.current) return;
    hydratedRef.current = true;

    const fh = (patient as { familyHistory?: PersistedFamilyHistory | null })
      ?.familyHistory;
    if (!fh) return;

    const next: Partial<FamilyHistoryState> = {};

    // API returns array of { ancestry, id, ... } objects — extract the enum values
    if (Array.isArray(fh.ancestryHistory) && fh.ancestryHistory.length > 0) {
      next.ancestryHistory = fh.ancestryHistory
        .map((a) => a.ancestry)
        .filter((a): a is AncestryEnum => Boolean(a));
      next._originalAncestryHistoryIds = fh.ancestryHistory
        .map((a) => a.id)
        .filter((id): id is string => Boolean(id));
    }

    if (fh.hasDoneDNAAncestryReport != null) {
      next.hasDoneDNAAncestryReport = fh.hasDoneDNAAncestryReport;
    }
    if (fh.dnaAncestryDocumentId != null) {
      next.dnaAncestryDocumentId = fh.dnaAncestryDocumentId;
    }
    if (fh.dnaAncestryReportDate != null) {
      next.dnaAncestryReportDate = fh.dnaAncestryReportDate;
    }

    if (fh.adoptionStatus != null) {
      next.adoptionStatus = fh.adoptionStatus;
    }

    if (fh.hasRelativeFacingFertilityIssues != null) {
      next.hasRelativeFacingFertilityIssues =
        fh.hasRelativeFacingFertilityIssues;
    }

    // API returns array of { familyMember, id, ... } objects — extract enum values
    if (
      Array.isArray(fh.familyMemberWithFertilityIssues) &&
      fh.familyMemberWithFertilityIssues.length > 0
    ) {
      next.familyMemberWithFertilityIssues = fh.familyMemberWithFertilityIssues
        .map((m) => m.familyMember)
        .filter((m): m is FamilyMemberEnum => Boolean(m));
      next._originalFamilyMemberIds = fh.familyMemberWithFertilityIssues
        .map((m) => m.id)
        .filter((id): id is string => Boolean(id));
    }

    if (fh.hasMotherTakenDESDuringPregnancy != null) {
      next.hasMotherTakenDESDuringPregnancy =
        fh.hasMotherTakenDESDuringPregnancy;
    }

    if (fh.diseasesMothersSide != null) {
      next.diseasesMothersSide = fh.diseasesMothersSide;
    }
    if (fh.diseasesFathersSide != null) {
      next.diseasesFathersSide = fh.diseasesFathersSide;
    }

    if (Object.keys(next).length > 0) {
      setState((prev) => ({ ...prev, ...next }));
    }
  }, [patient, isLoading]);

  const updateState = useCallback((updates: Partial<FamilyHistoryState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  return (
    <FamilyHistoryContext.Provider value={{ state, updateState }}>
      {children}
    </FamilyHistoryContext.Provider>
  );
}

export function useFamilyHistory() {
  const context = useContext(FamilyHistoryContext);
  if (!context) {
    throw new Error(
      "useFamilyHistory must be used within FamilyHistoryProvider",
    );
  }
  return context;
}
